import React, { useEffect, useState } from 'react';
import { apiFetch } from '../lib/api';
import { exportCsv } from '../lib/csv';
import { Loader2, AlertCircle, Eye, MousePointerClick, Ticket, Percent, MapPin, BadgeCheck } from 'lucide-react';

interface Offer {
  id: number;
  key: string;
  region: 'local' | 'mainland';
  restaurantName: string;
  title: string;
}

interface OfferDetail {
  id: number;
  key: string;
  region: string;
  restaurantName: string;
  title: string;
  maxClaims: number | null;
  validUntil: number | null;
  isActive: boolean;
  impressions: number;
  claimClicks: number;
  claims: number;
  redeemed: number;
  shares?: number;
  shareByMethod?: Record<string, number>;
  clickToClaimRate: string;
  impressionToClickRate: string;
  claimToRedeemRate: string;
}

interface OfferAnalyticsResponse {
  offerKey: string | null;
  startDate: number;
  endDate: number;
  summary: {
    totalOffers: number;
    totalImpressions: number;
    totalClicks: number;
    totalClaims: number;
    totalRedeemed: number;
    overallClickToClaimRate: string;
    overallImpressionToClickRate: string;
    overallClaimToRedeemRate: string;
  };
  details: OfferDetail[];
}

interface Props {
  offers: Offer[];
}

function getDateRange(days: number): { start: string; end: string } {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - days);
  start.setHours(0, 0, 0, 0);
  end.setHours(23, 59, 59, 999);
  return {
    start: start.toISOString().split('T')[0],
    end: end.toISOString().split('T')[0],
  };
}

function toUnixSeconds(dateStr: string): number {
  return Math.floor(new Date(dateStr).getTime() / 1000);
}

function formatDate(ts: number): string {
  return new Date(ts * 1000).toLocaleDateString('zh-HK');
}

const OfferAnalyticsReport: React.FC<Props> = ({ offers }) => {
  const [selectedOffer, setSelectedOffer] = useState<string>('');
  const [days, setDays] = useState<number>(30);
  const [startDate, setStartDate] = useState<string>(getDateRange(30).start);
  const [endDate, setEndDate] = useState<string>(getDateRange(30).end);
  const [data, setData] = useState<OfferAnalyticsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  React.useEffect(() => {
    if (offers.length > 0 && !selectedOffer) {
      setSelectedOffer('');
    }
  }, [offers, selectedOffer]);

  useEffect(() => {
    const { start, end } = getDateRange(days);
    setStartDate(start);
    setEndDate(end);
  }, [days]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const start = toUnixSeconds(startDate);
        const end = toUnixSeconds(endDate) + 86399;
        const offerKeyParam = selectedOffer ? `&offerKey=${encodeURIComponent(selectedOffer)}` : '';
        const res = await apiFetch(
          `/api/admin/analytics/offers?startDate=${start}&endDate=${end}${offerKeyParam}`
        );
        if (!res.ok) throw new Error('載入報表失敗');
        const json = await res.json();
        setData(json.data);
      } catch (err: any) {
        setError(err.message || '載入失敗');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedOffer, startDate, endDate]);

  if (offers.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-8 text-center">
        <p className="text-gray-500 dark:text-gray-400">尚未有任何優惠，無法顯示成效報表。</p>
      </div>
    );
  }

  const displayDetails = data?.details || [];

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 md:p-6 flex flex-col md:flex-row gap-4 items-start md:items-end">
        <div className="flex-1 w-full md:w-auto">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">選擇優惠</label>
          <select
            value={selectedOffer}
            onChange={(e) => setSelectedOffer(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white text-sm"
          >
            <option value="">全部匯總</option>
            {offers.map((o) => (
              <option key={o.key} value={o.key}>{o.restaurantName} · {o.title}</option>
            ))}
          </select>
        </div>

        <div className="w-full md:w-auto">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">快速範圍</label>
          <select
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white text-sm"
          >
            <option value={7}>最近 7 天</option>
            <option value={30}>最近 30 天</option>
            <option value={90}>最近 90 天</option>
            <option value={0}>自訂</option>
          </select>
        </div>

        <div className="flex gap-2 w-full md:w-auto">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">開始</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => { setDays(0); setStartDate(e.target.value); }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white text-sm"
            />
          </div>
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">結束</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => { setDays(0); setEndDate(e.target.value); }}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 dark:text-white text-sm"
            />
          </div>
        </div>
      </div>

      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-start gap-2">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {loading ? (
        <div className="min-h-[40vh] flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-red-600" />
        </div>
      ) : data ? (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <KpiCard icon={Eye} label="曝光次數" value={data.summary.totalImpressions} color="blue" />
            <KpiCard icon={MousePointerClick} label="領取點擊" value={data.summary.totalClicks} color="green" />
            <KpiCard icon={Ticket} label="實際領取" value={data.summary.totalClaims} color="red" />
            <KpiCard icon={BadgeCheck} label="已兌換" value={data.summary.totalRedeemed} color="green" />
            <KpiCard icon={Percent} label="領取 → 兌換" value={data.summary.overallClaimToRedeemRate} color="gray" />
          </div>

          {/* Details Table */}
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">優惠明細</h3>
              <button
                onClick={() =>
                  exportCsv(`offer-analytics-${new Date().toISOString().slice(0, 10)}`, [
                    ['餐廳', '優惠', '地區', '曝光', '點擊', '實際領取', '已兌換', '點擊率', '領取率', '兌換率', '名額'],
                    ...displayDetails.map((o) => [
                      o.restaurantName, o.title, o.region === 'mainland' ? '內地' : '香港',
                      o.impressions, o.claimClicks, o.claims, o.redeemed,
                      o.impressionToClickRate, o.clickToClaimRate, o.claimToRedeemRate,
                      o.maxClaims ? `${o.claims}/${o.maxClaims}` : '—',
                    ]),
                  ])
                }
                disabled={displayDetails.length === 0}
                className="text-xs px-3 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-40"
              >
                ⬇ 匯出 CSV
              </button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-gray-700 dark:text-gray-300">餐廳 / 優惠</th>
                    <th className="px-4 py-3 text-left font-medium text-gray-700 dark:text-gray-300">地區</th>
                    <th className="px-4 py-3 text-center font-medium text-gray-700 dark:text-gray-300">曝光</th>
                    <th className="px-4 py-3 text-center font-medium text-gray-700 dark:text-gray-300">點擊</th>
                    <th className="px-4 py-3 text-center font-medium text-gray-700 dark:text-gray-300">分享 📤</th>
                    <th className="px-4 py-3 text-center font-medium text-gray-700 dark:text-gray-300">實際領取</th>
                    <th className="px-4 py-3 text-center font-medium text-gray-700 dark:text-gray-300">已兌換</th>
                    <th className="px-4 py-3 text-center font-medium text-gray-700 dark:text-gray-300">點擊率</th>
                    <th className="px-4 py-3 text-center font-medium text-gray-700 dark:text-gray-300">領取率</th>
                    <th className="px-4 py-3 text-center font-medium text-gray-700 dark:text-gray-300">兌換率</th>
                    <th className="px-4 py-3 text-center font-medium text-gray-700 dark:text-gray-300">名額</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {displayDetails.map((offer) => (
                    <tr key={offer.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900 dark:text-white">{offer.restaurantName}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">{offer.title}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full ${
                          offer.region === 'mainland'
                            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                            : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                        }`}>
                          <MapPin className="w-3 h-3" />
                          {offer.region === 'mainland' ? '內地' : '香港'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center text-gray-700 dark:text-gray-300">{offer.impressions.toLocaleString()}</td>
                      <td className="px-4 py-3 text-center text-gray-700 dark:text-gray-300">{offer.claimClicks.toLocaleString()}</td>
                      <td className="px-4 py-3 text-center font-medium text-blue-600 dark:text-blue-400">
                        <span title={offer.shareByMethod ? Object.entries(offer.shareByMethod).map(([m, n]) => `${m}: ${n}`).join(' / ') : undefined}>
                          {(offer.shares ?? 0).toLocaleString()}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center font-medium text-gray-900 dark:text-white">{offer.claims.toLocaleString()}</td>
                      <td className="px-4 py-3 text-center font-medium text-green-700 dark:text-green-400">{offer.redeemed.toLocaleString()}</td>
                      <td className="px-4 py-3 text-center text-gray-700 dark:text-gray-300">{offer.impressionToClickRate}</td>
                      <td className="px-4 py-3 text-center text-gray-700 dark:text-gray-300">{offer.clickToClaimRate}</td>
                      <td className="px-4 py-3 text-center text-gray-700 dark:text-gray-300">{offer.claimToRedeemRate}</td>
                      <td className="px-4 py-3 text-center text-gray-700 dark:text-gray-300">
                        {offer.maxClaims ? `${offer.claims}/${offer.maxClaims}` : '—'}
                      </td>
                    </tr>
                  ))}
                  {displayDetails.length === 0 && (
                    <tr>
                      <td colSpan={11} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                        此範圍內未有數據
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
};

function KpiCard({
  icon: Icon,
  label,
  value,
  color,
}: {
  icon: React.ElementType;
  label: string;
  value: number | string;
  color: 'blue' | 'green' | 'gray' | 'red';
}) {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
    green: 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    gray: 'bg-gray-50 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
    red: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  };

  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
      <div className={`w-8 h-8 rounded-lg flex items-center justify-center mb-3 ${colorClasses[color]}`}>
        <Icon className="w-4 h-4" />
      </div>
      <p className="text-sm text-gray-500 dark:text-gray-400">{label}</p>
      <p className="text-xl font-bold text-gray-900 dark:text-white">{typeof value === 'number' ? value.toLocaleString() : value}</p>
    </div>
  );
}

export default OfferAnalyticsReport;
